import React from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import RequestBuilder from 'Resources/RequestBuilder';
import ComparisonTypes from 'Resources/ComparisonTypes';
import Activity from 'App/resources/Model/Activity';
import ActivityResource from 'Resources/Activity';
import { Checkbox, Column, Flex, Icon, IconName, Text, Tooltip } from '@upsales/components';
import { onClick, setTypeProps } from 'App/pages/TodoList/TodoListTableRow/TodoListTableRow';
import SimpleListView from '../../ClientCard/SimpleListView';
import T from 'Components/Helpers/translate';
import moment from 'moment';
import Todo from 'App/resources/Model/Todo';
import '../../ClientCard/ContentPages/Activities/Activities.scss';
import { openDrawer } from 'Services/Drawer';
import Attribute, { DisplayType, Type } from 'App/babel/attributes/Attribute';
import { getContactLink } from 'App/pages/TodoList/TodoListTableRow/TodoListColumns';
import TodoListTableRowQuickActions from 'App/pages/TodoList/TodoListTableRow/TodoListTableRowQuickActions';
import Button from '@upsales/components/Button/Button';
import DefaultNoData from 'App/components/ListView/DefaultNoData';
import Mail from 'App/resources/Model/Mail';
import CreateTaskButton from 'App/components/CreateTaskButton';
import { CardEntity } from './types';
import AppService from 'App/legacy/AppService';

// `iconCol` carries the type icon, and for a to-do the completion checkbox. It belongs ahead of
// the description it marks - both the Angular card and the TodoList page put it there - not
// stranded between Description and Type, where it read as part of the type.
//
// The contact column earns its place on a client card, where the rows are spread across people.
// On a contact card every row already involves the card's contact, and the column renders
// `contacts[0]` - which the query does not pin to that contact - so it could headline a colleague
// who happened to be first on the activity. The Angular card showed no such column.
const getColumns = (cardType: CardEntity['type']) => [
	'date',
	'iconCol',
	'description',
	'type',
	...(cardType === 'client' ? ['contacts'] : []),
	'users',
	'todoTools'
];

type TodoOrMail = Todo &
	OptionalPropGroup<{
		subject: string;
		icon: IconName;
		to: string;
		mailType: string;
	}> & {
		endDate?: string | Date;
	};

const activityToTodo: (a: Activity | Mail) => TodoOrMail = activity => {
	const todo = {
		...activity,
		type: 'appointment',
		hot: false,
		weblinkUrl: null,
		location: null
	} as TodoOrMail;

	const todoTypes = AppService.getTodoTypes();
	const activityTypes = AppService.getActivityTypes('activity');

	if ('to' in activity) {
		todo.description = todo.subject!;
		todo.time = moment(todo.date).format('HH:mm:ss') ?? null;
		todo.mailType = activity.type;
		todo.icon = 'envelope';
		todo.type = 'legacy';
	} else if (activity.activityType.id === todoTypes.TODO.id) {
		todo.type = 'todo';
	} else if (activity.activityType.id === todoTypes.PHONE_CALL.id) {
		todo.type = 'phonecall';
	} else if (activityTypes.map(a => a.id).includes(activity.activityType.id)) {
		todo.icon = 'activity';
		todo.type = 'legacy';
	}
	return todo;
};

const generateGetDataFn =
	(open: boolean, cardEntity: CardEntity) =>
	(
		requestBuilder: RequestBuilder
	): Promise<{
		data: Todo[];
		metadata: {
			total: number;
		};
	}> => {
		const resource = Tools.ActivityList;

		requestBuilder.addSort(resource.attr.date, false);
		requestBuilder.addSort(resource.attr.id, false);
		if (cardEntity.type === 'client') {
			requestBuilder.addFilter(resource.attr.client.attr.id, ComparisonTypes.Equals, cardEntity.id);
		} else {
			requestBuilder.addFilter({ field: 'contact.id' }, ComparisonTypes.Equals, cardEntity.id);
		}
		requestBuilder.addFilter(resource.attr.projectPlan.attr.id, ComparisonTypes.Equals, null);

		const orBuilder = requestBuilder.orBuilder();
		orBuilder.next();
		orBuilder.addFilter(resource.attr.isAppointment, ComparisonTypes.Equals, false);
		orBuilder.addFilter(resource.attr.closeDate, open ? ComparisonTypes.Equals : ComparisonTypes.NotEquals, null);
		orBuilder.next();
		orBuilder.addFilter(resource.attr.isAppointment, ComparisonTypes.Equals, true);
		if (open) {
			// An appointment is open while it is still planned and not yet over.
			orBuilder.addFilter({ field: 'outcome' }, ComparisonTypes.Equals, 'planned');
			orBuilder.addFilter(resource.attr.endDate, ComparisonTypes.GreaterThanEquals, new Date());
		} else {
			// History has to be the exact complement of that, which takes two branches:
			// an appointment that is over whatever its outcome, plus one resolved ahead
			// of its end date. Matching on `outcome != planned` alone dropped every past
			// appointment still sitting on the default outcome — nobody has to mark a
			// meeting afterwards — so those showed in neither list and vanished from
			// the card entirely.
			orBuilder.addFilter(resource.attr.endDate, ComparisonTypes.LessThan, new Date());
			orBuilder.next();
			orBuilder.addFilter(resource.attr.isAppointment, ComparisonTypes.Equals, true);
			orBuilder.addFilter({ field: 'outcome' }, ComparisonTypes.NotEquals, 'planned');
		}

		// Sent emails
		if (!open) {
			orBuilder.next();
			orBuilder.addFilter({ field: 'isMap' }, ComparisonTypes.Equals, false);
			orBuilder.next();

			let groupBuilder = orBuilder.groupBuilder();
			groupBuilder.addFilter({ field: 'isMap' }, ComparisonTypes.Equals, true);
			groupBuilder.addFilter({ field: 'groupMailId' }, ComparisonTypes.Equals, null);
			groupBuilder.done();
			orBuilder.next();
			groupBuilder = orBuilder.groupBuilder();
			groupBuilder.addFilter({ field: 'isMap' }, ComparisonTypes.Equals, true);
			groupBuilder.addFilter({ field: 'groupMailId' }, ComparisonTypes.Equals, 0);
			groupBuilder.done();
		}
		orBuilder.done();

		const built = requestBuilder.build();
		return resource.find(built).then(res => ({ ...res, data: res.data.map(activityToTodo) }));
	};

const isOverdue = (todo: TodoOrMail) => {
	const date = todo.endDate ?? todo.date;
	if (todo.type === 'appointment' || todo.time) {
		return moment(date).isBefore(moment());
	}
	return moment(date).isBefore(moment().subtract(1, 'days').endOf('day'));
};

const getDateFlags = (item: TodoOrMail) => {
	const hasDate = !!item.date;
	const future = hasDate && !isOverdue(item);
	const closed = !!item.closeDate || (item.type === 'appointment' && !future);
	const isMail = 'to' in item;
	return { hasDate, future, closed, isMail };
};

const getMailTitle = (item: TodoOrMail) => {
	switch (item.mailType) {
		case 'in':
			return T('mail.mailIn');
		case 'out':
			return T('mail.mailOut');
		case 'err':
			return `(${T('mail.status.ERROR')})`;
	}
	return T('mail.mail');
};

const customColumnRenders: GetProp<typeof SimpleListView<TodoOrMail>, 'customColumnRenders'> = {
	date: item => {
		const { future, hasDate, closed, isMail } = getDateFlags(item);
		const typeProps = setTypeProps(item);
		return (
			<Text color={future ? 'black' : hasDate ? 'red' : 'grey-9'} size="sm" italic={closed || !hasDate || isMail}>
				{typeProps.timeSubTitle || T('default.noDate')}
			</Text>
		);
	},
	description: item => {
		// An activity's `description` and a mail's `subject` are both nullable columns,
		// and the mail branch above assigns the subject through a non-null assertion —
		// so this arrives empty for a mail sent without a subject. The tooltip below
		// already hedged with `?? ''`; the length read did not, and it threw, taking the
		// whole card down instead of leaving one cell blank.
		const description = item.description ?? '';
		return description.length < 34 ? (
			<Text size="sm">{description}</Text>
		) : (
			<Tooltip title={description} disabled={description.length < 31}>
				<Text size="sm">{description.slice(0, 30) + '...'}</Text>
			</Tooltip>
		);
	},
	type: item => {
		const { isMail } = getDateFlags(item);
		const typeProps = setTypeProps(item);
		return (
			<Column>
				<Text size="sm">
					{isMail ? getMailTitle(item) : typeProps.isTodo ? T('default.todo') : item.activityType.name}
				</Text>
				{item.notes ? (
					<Tooltip
						title={item.notes.slice(0, 400) + (item.notes.length > 400 ? '...' : '')}
						position="left"
						disabled={item.notes.length < 21}
					>
						<Text color="grey-10" italic>
							{item.notes.slice(0, 20) + (item.notes.length > 20 ? '...' : '')}
						</Text>
					</Tooltip>
				) : null}
			</Column>
		);
	},
	contacts: item =>
		getContactLink(item.contacts) ?? (
			<Text size="sm" italic color="grey-9">
				{T('default.noContact')}
			</Text>
		),
	todoTools: item => {
		const { closed, isMail } = getDateFlags(item);
		const typeProps = setTypeProps(item);
		return !isMail ? (
			<Flex alignItems="center" justifyContent="flex-end">
				{closed || item.type === 'appointment' ? null : (
					<Tooltip
						title={T(
							item.priority === 3
								? typeProps.isTodo
									? 'todo.prioTodo'
									: 'todo.prioCall'
								: 'activity.markAsImportant'
						)}
					>
						<Button
							type="link"
							color={item.priority === 3 ? 'red' : 'grey'}
							onClick={e => {
								e?.stopPropagation();
								ActivityResource.save({ id: item.id, priority: item.priority === 3 ? 0 : 3 });
							}}
						>
							<Icon name="flag" />
						</Button>
					</Tooltip>
				)}
				<TodoListTableRowQuickActions todo={item} />
			</Flex>
		) : null;
	},
	iconCol: item => {
		const typeProps = setTypeProps(item);
		if (item.type === 'legacy') {
			typeProps.icon = item.icon!;
		}

		return typeProps.isTodo ? (
			<Checkbox
				size="xs"
				checked={!!item.closeDate}
				onClick={e => {
					e.stopPropagation();
					ActivityResource.save({ id: item.id, closeDate: item.closeDate ? null : new Date() });
				}}
			/>
		) : (
			<Icon color="grey-13" name={typeProps.icon || ('' as IconName)} />
		);
	}
};

// For some attributes we only care about setting the column title
const TitleAttribute = (title: string = '', sortable: string | false = false) =>
	Attribute({
		field: '',
		title,
		type: Type.String,
		sortable
	});

const CardActivityListView = ({ cardEntity, open }: { cardEntity: CardEntity; open: boolean }) => {
	const classes = new BemClass('ClientCardActivities');
	return (
		<SimpleListView<Todo>
			classes={classes.mod(open ? 'open' : 'past')}
			getData={(rb: RequestBuilder) => generateGetDataFn(open, cardEntity)(rb)}
			columns={getColumns(cardEntity.type)}
			customColumnRenders={customColumnRenders}
			renderNoData={() => <DefaultNoData subtitle="" formatNoData={() => T('default.noResults')} />}
			onRowClick={item => () => {
				const { isMail } = getDateFlags(item);
				if (isMail) {
					openDrawer('SentMail', { mail: { id: item.id } });
				} else {
					onClick(item);
				}
			}}
			rowStyles={{
				inactive: () => !open,
				delayed: item => {
					const { hasDate, future, closed, isMail } = getDateFlags(item);
					return !closed && !future && hasDate && !isMail;
				}
			}}
			initialSorting={[{ attribute: 'date', ascending: false }]}
			onChange={prov => setTimeout(() => prov.reloadTable({ silent: true }), 1200)}
			broadcastType={['activity', 'appointment']}
			attributes={{
				users: Attribute({
					field: 'users',
					title: 'default.users',
					placeholder: 'default.noUser',
					type: Type.Array,
					displayType: DisplayType.Users,
					sortable: 'user.name'
				}),
				date: TitleAttribute('default.date', 'date'),
				iconCol: TitleAttribute(''),
				type: TitleAttribute('default.type', 'activityType.name'),
				todoTools: TitleAttribute(''),
				description: TitleAttribute('default.description', 'description'),
				contacts: TitleAttribute('default.contact', 'contact.name')
			}}
			renderHeader={provided => (
				<Flex className={classes.elem('header').b()} justifyContent="space-between">
					<Flex gap="u2">
						<Text className={classes.elem('headerTitle').b()} size="xl" bold>
							{open ? T('default.openActivities') : T('default.history')}
						</Text>
						<Text className={classes.elem('headerAmount').b()} size="lg" color="grey-10">
							{`(${provided.total})`}
						</Text>
					</Flex>
					{open ? (
						<CreateTaskButton
							size="sm"
							createArgs={
								cardEntity.type === 'client'
									? { client: cardEntity.data }
									: { contact: cardEntity.data }
							}
						/>
					) : null}
				</Flex>
			)}
		/>
	);
};

const SharedActivities = ({ cardEntity }: { cardEntity: CardEntity }) => {
	if (!cardEntity.data) return null;
	return (
		<>
			<CardActivityListView cardEntity={cardEntity} open={true} />
			<CardActivityListView cardEntity={cardEntity} open={false} />
		</>
	);
};

export default SharedActivities;
